const DEFAULT_INTERVAL_MS = 2_000;
const DEFAULT_MAX_RETRIES = 3;

function retryDelay(attempt, intervalMs) {
  return Math.min(10_000, intervalMs * (2 ** Math.max(0, attempt - 1)));
}

/**
 * Serialize batch-status polling and retry transient transport failures.
 * The caller owns rendering and state updates; this controller only owns timer
 * lifecycle, duplicate-poll prevention, and retry policy.
 */
export function createBatchPollController({
  getBatchJob,
  isCurrentJobId,
  onResponse,
  onPending,
  onCompleted,
  onFailed,
  onUnknown,
  onRetry,
  onError,
  intervalMs = DEFAULT_INTERVAL_MS,
  maxRetries = DEFAULT_MAX_RETRIES,
  timers = globalThis,
} = {}) {
  let timer = null;
  let activeRequest = null;
  let retryAttempts = 0;
  let generation = 0;

  function clearTimer() {
    if (timer !== null) timers.clearTimeout(timer);
    timer = null;
  }

  function clear() {
    generation += 1;
    clearTimer();
    retryAttempts = 0;
    activeRequest?.controller.abort();
    activeRequest = null;
  }

  function schedule(jobId, delay = intervalMs) {
    const scheduledGeneration = generation;
    clearTimer();
    timer = timers.setTimeout(() => {
      timer = null;
      if (scheduledGeneration === generation) void poll(jobId);
    }, delay);
  }

  async function poll(jobId) {
    if (!jobId || activeRequest) return;
    const pollGeneration = generation;
    const controller = new AbortController();
    const request = { jobId, controller };
    activeRequest = request;

    try {
      const data = await getBatchJob(jobId, { signal: controller.signal });
      if (pollGeneration !== generation || !isCurrentJobId(jobId)) return;

      retryAttempts = 0;
      onResponse?.(data);
      if (data.status === 'running' || data.status === 'queued') {
        onPending?.(data);
        schedule(jobId);
        return;
      }
      if (data.status === 'completed') {
        onCompleted?.(data);
        return;
      }
      if (data.status === 'failed') {
        onFailed?.(data);
        return;
      }
      onUnknown?.(data);
    } catch (error) {
      if (pollGeneration !== generation || controller.signal.aborted || !isCurrentJobId(jobId)) return;

      retryAttempts += 1;
      if (retryAttempts <= maxRetries) {
        const delay = retryDelay(retryAttempts, intervalMs);
        onRetry?.(error, { attempt: retryAttempts, maxRetries, delay });
        schedule(jobId, delay);
        return;
      }
      onError?.(error, { attempts: retryAttempts });
    } finally {
      if (activeRequest === request) {
        activeRequest = null;
      }
    }
  }

  return { clear, poll, schedule };
}
